import React, { useState } from "react";
import Select from 'react-select';
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { setUser, setLoginStatus } from "../../slices/auth";
import InputComponent from "../UI/input_component/input_component";
import { useRegisterMutation, useLoginMutation } from "../../slices/authApi";
import InActiveInfoModal from "../UI/inactive_info_modal/inactive_info_modal";
import InputModal from "../UI/input_modal/input_modal";
import { responsibilitiesOptions, teacherGradeOptions, teacherSubjectOptions } from "../../constants";
import classes from "./register_page.module.css";
import ActionButton from "../UI/ActionButton/ActionButton";

const optionsMapper = (options) => {
  return options.map(option => ({ label: option, value: option }))
}

// Dictionary mapping prefixes to their respective agreement texts
const AGREEMENTS = {
  cy: {
    field: 'pii_agreement',
    text: `By creating an account and using the tool, I acknowledge that all information entered will 
           become part of the product's model. I agree not to enter any Personally Identifiable 
           Information (PII) about myself or my students and to adhere to City Year's policies on 
           Protected or Sensitive information.`,
  },
  bt: {
    field: 'pii_agreement',
    text: `Acknowledgment of Limited Pilot License. By creating an account and accessing the ClassGenius platform, 
           I acknowledge and agree that unless Breakthrough Central Texas ("Breakthrough") proceeds to Phase 2 
           of the Pilot, the platform may only be used to create supplementary materials for Breakthrough's 
           2025 Middle School Summer Academy science curriculum. Any materials generated using the platform 
           may only be used during the 2025 Summer Academy. My access is provided solely for the duration of 
           this Pilot and does not grant any rights to continued use of the platform or its outputs beyond the 
           2025 Summer Academy.
           By proceeding, I confirm that I understand and agree to these conditions.`,
  },
};

const AgreementCheckbox = React.memo(({ agreementText, checked, onChange, textAlign = 'left' }) => {
  const textAlignClass =
    textAlign === 'justify'
      ? classes['agreement-text-justify']
      : classes['agreement-text-left'];
  return (
    <>
      <label className={classes["pii-checkbox-container"]}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          
        />
        <span className={classes["checkmark"]}></span>
        <span className={`${classes["agreement-text"]} ${textAlignClass}`}>{agreementText}</span>
      </label>
    </>
  );
});

// Set default props
AgreementCheckbox.defaultProps = {
  checked: false,
  onChange: () => {},
};

const RegisterPage = ({ setError }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [register] = useRegisterMutation();
  const [login] = useLoginMutation();
  const [infoModal, showInfoModal] = useState(false);
  const [agreementModal, showAgreementModal] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    affiliation: "",
    grades_taught: [],
    subjects_taught: [],
    access_code: "",
    responsibilities: null,
    terms: false,
    pii_agreement: false,
    referral: null,
    other_referral: "",
  });

  // Utility function to find matching prefix
  const getAgreementByPrefix = (accessCode) => {
    const lowerAccessCode = accessCode.toLowerCase();
    for (const prefix in AGREEMENTS) {
      if (lowerAccessCode.startsWith(prefix)) {
        return AGREEMENTS[prefix];
      }
    }
    return null;
  };

  const handleRegister = async (formData) => {
    const agreement = getAgreementByPrefix(formData?.access_code);

    if (agreement && !formData[agreement.field]) {
      showAgreementModal(true);
      return;
    }
    try {
      const response = await register(JSON.stringify(formData)).unwrap();
      if (response["user_state"] === "Pending") {
        showInfoModal(true)
      } else if (response["user_state"] === "Active") {
        const userData = await login().unwrap();
        dispatch(setUser({ userData }));
        dispatch(setLoginStatus(true));
      }
      navigate("..");
    } catch (err) {
      if (!err?.status) {
        setError("No Server Response");
      } else if (err.status === 401) {
        setError("Unauthorized. Reach out to team@classgenius.ai for access.");
      } else if (err.status === 400 && err?.data?.access_code) {
        // Handle invalid access code specifically
        setError("Invalid access code. Contact team@classgenius.ai if you would like to request access.");
      } else {
        let err_message = "";
        if (err?.data?.message) {
          err_message = err.data.message;
        } else if (err?.data?.error) {
          err_message = err.data.error;
        } else {
          err_message = err.status + " Server Error";
        }

        setError(err_message);
      }
    }
  };

  // Determine current agreement text based on access code
  const currentAgreement = getAgreementByPrefix(formData.access_code) || {
    text: '',
    field: '',
  };

  const handleInput = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormError = () => {
    const errors = {
      'terms': 'Please accept the terms and conditions first'
    }
    const { terms } = formData || {};
    if (!terms) return errors['terms'];
  }


  const submitFormData = () => {
    const error = handleFormError();
    if (error) {
      setError(error);
      return;
    } else {
      setError('');
    }
  
    const updatedFormData = {
      ...formData,
      responsibilities: formData?.responsibilities?.value || '',
      grades_taught: formData?.grades_taught?.map((item) => item.value) || [],
      subjects_taught: formData?.subjects_taught?.map((item) => item.value) || [],
      referral_source: formData?.referral?.value === 'Other' 
        ? formData.other_referral 
        : formData?.referral?.value,
    };

    handleRegister(updatedFormData);
  };
  
  const onSubmit = (e) => {
    e.preventDefault();
    submitFormData();
  };
  

  return (
    <>
      <div className={classes.signUpContainer}>
        <h1 className={classes.singUpText}>Almost there! <br/> Please fill out these few fields to complete your registration.</h1>
        <form onSubmit={onSubmit}>
          <input type="submit" style={{ display: "none" }} />
          <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>School you're associated with*</label>
            <InputComponent
              type="text"
              name="affiliation"
              isRequired={true}
              onChangeHandler={({ target }) => handleInput('affiliation', target.value)}
              title="Please indicate the school you are associated with "
            />
          </div>
          <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>Your role</label>
            <Select
              options={optionsMapper(responsibilitiesOptions)}
              name="responsibilities"
              onChange={(option) => handleInput('responsibilities', option)}
              value={formData?.responsibilities}
              className={classes.selectBox}
            />
          </div>
          <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>What grades do you teach? (Select all that apply)</label>
            <Select
              options={optionsMapper(teacherGradeOptions)}
              onChange={(options) => handleInput('grades_taught', options)}
              value={formData?.grades_taught}
              isMulti
              className={classes.selectBox}
            />
          </div>
          <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>What subjects do you teach? (Select all that apply)</label>
            <Select
              options={optionsMapper(teacherSubjectOptions)}
              onChange={(options) => handleInput('subjects_taught', options)}
              value={formData?.subjects_taught}
              isMulti
              className={classes.selectBox}
            />
          </div>
          {/* <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>How did you learn about us?</label>
            <Select
              options={optionsMapper(referralOptions)}
              onChange={(options) => handleInput('referral', options)}
              value={formData?.referral}
              className={classes.selectBox}
            />
          </div>
          {formData.referral?.value === 'Other' && (
              <div className={classes["input-container"]}>
                <label className={classes.fieldLabel}>Please specify:</label>
                <InputComponant
                  type="text"
                  name="other_referral"
                  value={formData.other_referral}
                  onChangeHandler={({ target }) => handleInput('other_referral', target.value)}
                  isRequired={true}
                />
              </div>
            )} */}
          <div className={classes["input-container"]}>
            <label className={classes.fieldLabel}>Enter your access code here*</label>
            <InputComponent
              type="text"
              name="access_code"
              onChangeHandler={({ target }) => handleInput('access_code', target.value)}
              title="Please enter the access code you received from ClassGenius"
            />
          </div>
          <div className={classes["input-container"]}>
            <label className={classes['checkbox-container']}>
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={({ target }) => handleInput('terms', target.checked)}
                
              />
              <span className={classes['checkmark']} ></span><span className={`${classes["agreement-text"]} ${classes['agreement-text-left']}`}>I agree to the <Link to="/terms" target="_blank" className={classes.link}>Terms and Conditions</Link> and <Link to="/privacy" target="_blank" className={classes.link}>Privacy Policy</Link></span>
            </label>
            {currentAgreement.text && (
              <AgreementCheckbox
                agreementText={currentAgreement.text}
                checked={formData[currentAgreement.field]}
                onChange={({ target }) => handleInput(currentAgreement.field, target.checked)}
                textAlign="justify"
              />
            )}
          </div>
          <div className={classes["button-container"]}>
            <ActionButton text="Create my account" handleOnClick={submitFormData} buttonType="button" />
          </div>
        </form>
      </div>

      {/* Modals */}
      <InActiveInfoModal
        continueHandler={() => showInfoModal(false)}
        isOpen={infoModal}
        btnText={"Okay"}
        heading="Access code required"
        message="Thank you for your interest in ClassGenius. If you'd like an access code, please reach out to us at "
        email="team@classgenius.ai"
      />
      {/* Legal agreement modal for some user groups (Cityyear etc) */}
      <InputModal
        modalProps={{
          isOpen: agreementModal,
          description: ``,
          rightBtnHandler: () => {
            showAgreementModal(false);
            submitFormData();          
          },
          buttons: {
            rightBtn: "Continue",
          },
        }}
        InputComponent={AgreementCheckbox}
        inputProps={{
          agreementText: currentAgreement.text,
          checked: formData[currentAgreement.field],
          onChange: (e) => setFormData({
            ...formData,
            [currentAgreement.field]: e.target.checked,
          }),
          textAlign: 'justify',
        }}        
      />
    </>
  );
};
export default RegisterPage;
